import { LoginMethod, type LoginOption } from '@today/types'
import { Button } from '@today/ui-primitives'
import { type AuthLogo, AuthShellView, AuthStatusLine } from '../auth-shell/auth-shell.view'

/** The copy of {@link TenantLoginView}. Interpolated labels are functions. */
export interface TenantLoginLabels {
	title: (slug: string) => string
	subtitle: string
	checking: (slug: string) => string
	native: string
	sso: (name: string) => string
	ssoUnnamed: string
}

const DEFAULT_LABELS: TenantLoginLabels = {
	title: (slug) => `Sign in to ${slug}`,
	subtitle: "You'll be redirected to the secure sign-in page.",
	checking: (slug) => `Checking how ${slug} signs in…`,
	native: 'Sign in with email and password',
	sso: (name) => `Continue with ${name}`,
	ssoUnnamed: "Continue with your organization's single sign-on",
}

/**
 * Props for {@link TenantLoginView}.
 *
 * @property tenantSlug - The tenant the surface belongs to, from the URL.
 * @property options - The tenant's sign-in options in the server's order, or `null` while they resolve.
 * @property isLoading - True while the options resolve, or while the page redirects to the only option.
 * @property onSelect - Starts the chosen sign-in option.
 * @property logo - The bound brand's logo (never the tenant's — REQ-003 non-enumeration).
 * @property labels - The copy; any omitted label falls back to English.
 */
export interface TenantLoginViewProps {
	tenantSlug: string
	options: LoginOption[] | null
	isLoading?: boolean
	onSelect: (option: LoginOption) => void
	logo?: AuthLogo
	labels?: Partial<TenantLoginLabels>
}

/** The button label for one option. */
const labelOf = (option: LoginOption, copy: TenantLoginLabels): string => {
	if (option.method === LoginMethod.NATIVE) return copy.native
	return option.name ? copy.sso(option.name) : copy.ssoUnnamed
}

/**
 * Per-tenant login surface (TO-410 REQ-008; TO-361 login-surface REQ-002/003).
 * Pure: renders one button per sign-in option, in the order given, inside the
 * auth card. What it renders depends only on the slug and the options.
 * Resolving the options, and redirecting straight away when there is only one,
 * belong to the page's hook.
 */
export function TenantLoginView({
	tenantSlug,
	options,
	isLoading = false,
	onSelect,
	logo,
	labels,
}: TenantLoginViewProps) {
	const copy = { ...DEFAULT_LABELS, ...labels }
	return (
		<AuthShellView logo={logo} title={copy.title(tenantSlug)} subtitle={copy.subtitle}>
			{isLoading || options === null ? (
				<AuthStatusLine text={copy.checking(tenantSlug)} />
			) : (
				<ul className="flex w-full flex-col gap-ds-2">
					{options.map((option) => (
						<li key={option.identityProvider} className="flex flex-col">
							<Button
								variant={option.method === LoginMethod.NATIVE ? 'secondary' : 'primary'}
								className="h-auto min-h-ds-10 break-words"
								onPress={() => onSelect(option)}
							>
								{labelOf(option, copy)}
							</Button>
						</li>
					))}
				</ul>
			)}
		</AuthShellView>
	)
}
